import { AdditionalYields } from '../../../../types'

export const PRICE_DECIMALS = 18
export const RESERVE_MANTISSA_DECIMALS = 18

/**
 * Resolve a market's USD price from the flattened price map, keyed exactly the
 * way `selectAssetGroupPrices` produces it: `assetGroup` when the token-list
 * entry has one, else `<chainId>-<lowercase address>`. The Compound V2
 * converters used to read `prices[asset.assetGroup]` alone, which is `0` for
 * any token whose list entry has no `assetGroup` — native gas tokens above all
 * (FLR on Flare, AVAX, CRO, …). The oracle prices those correctly under the
 * fallback key, so their markets silently published `$0` TVL.
 */
export const resolveMarketPrice = (
  prices: { [asset: string]: number },
  asset: { assetGroup?: string; address?: string } | null | undefined,
  chainId: string,
): number => {
  const group = asset?.assetGroup
  if (group != null && prices[group] != null) return prices[group]
  const address = asset?.address?.toLowerCase()
  if (address != null) {
    const fallback = prices[`${chainId}-${address}`]
    if (fallback != null) return fallback
  }
  return 0
}

// Minimal shape we need from any reader
export type CTokenMeta = {
  collateralFactorMantissa?: string | bigint | number
  exchangeRateCurrent?: string | bigint | number
  totalSupply?: string | bigint | number
  totalBorrows?: string | bigint | number
  supplyRatePerBlock?: string | bigint | number
  /**
   * `getCash()` as the lens names it — `totalCash` on the Compound/Cream/
   * Venus/Sumer lenses, `cash` on Moonwell's. This is the WITHDRAWABLE
   * amount; see `cashLiquidity` in `./liquidity` for why it must not be
   * derived from deposits minus borrows.
   */
  totalCash?: string | bigint | number
  cash?: string | bigint | number
  borrowRatePerBlock?: string | bigint | number
  pausedActions?: unknown
  isListed?: boolean | string | number | bigint
  cTokenDecimals?: string | number | bigint
  isBorrowAllowed?: boolean | string | number | bigint
  depositRate: number
  variableBorrowRate: number
}

// Reader function signature (raw → normalized meta)
export type CTokenReader = (raw: any, token: any) => CTokenMeta

// Common converter options
export interface ConvertEntryOpts {
  entryRaw: any
  asset: any
  chainId: string
  lender: string
  prices: { [asset: string]: number }
  additionalYields: AdditionalYields
  cToken: string
}
